import { useEffect, useState } from 'react'
import { Select, Space, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { SEARCH_DEBOUNCE_MS } from '@/shared/lib/localList'
import { shortId } from '@/shared/lib/shortId'
// ⚠️ `SUBSCRIBER_SEARCH_LIMIT` bu yerga IMPORT QILINMAYDI: u faqat
// quyidagi izohda tilga olinadi va `{@link}` ichidagi nom KOD emas —
// eslint uni ishlatilmagan import deb sanardi. Izohda backtick bilan
// yozilgani `AgentSelect` va qolgan fayllardagi uslub bilan bir xil.
import { fetchSubscriber, searchSubscribers, type Subscriber } from './subscriberApi'

interface SubscriberSelectProps {
  value?: string | null
  onChange?: (value: string | null) => void
  disabled?: boolean
  placeholder?: string
}

/**
 * Abonent tanlash — LOGIN bo'yicha.
 *
 * <h2>⚠️ NEGA PAYDO BO'LDI</h2>
 * Filtr maydonlari abonentning UUID'ini SO'RARDI, operator esa uni
 * hech qayerdan ko'rmaydi: ro'yxatda ham, kartochkada ham login turadi.
 * Ya'ni maydon bor edi-yu, unga yetadigan yo'l yo'q edi — panel bo'ylab
 * takrorlanadigan naqsh (`AgentSelect` javadoc'idagi bilan bir xil).
 *
 * <h2>⚠️ QIDIRUV SERVERDA, KLIENTDA EMAS</h2>
 * `AgentSelect` butun daraxtni bir marta tortadi va uni klientda
 * filtrlaydi — o'nlab tugun uchun bu to'g'ri. Abonent boshqa tartib:
 * bugun 136 ta, ertaga mingtadan oshadi. Shuning uchun har terilgan
 * so'z serverga boradi (`searchSubscribers`) va javob
 * `SUBSCRIBER_SEARCH_LIMIT` qator bilan kesiladi.
 *
 * ⚠️ KESILGANI AYTILADI. Jimgina kesish eng yomon holat bo'lardi:
 * operator qidirgan abonentini ko'rmay "bunday abonent yo'q" degan
 * xulosaga kelardi — holbuki u ro'yxatning 31-qatorida turibdi.
 *
 * <h2>⚠️ LOGIN ASOSIY, ISM QO'SHIMCHA</h2>
 * Faqat ism ko'rsatilsa bir xil ismli ikki mijozni ajratib bo'lmasdi.
 * Login esa tenant ichida yagona (`subscriber_login_uq`).
 *
 * <h2>⚠️ TANLANGAN QIYMAT RO'YXATDA BO'LMASLIGI MUMKIN</h2>
 * Qiymat havola bilan ham keladi (`?f.ownerId=<uuid>`), qidiruv ro'yxati
 * esa faqat oxirgi so'rovning natijasi. Bunday qiymat uchun ALOHIDA
 * so'rov yuboriladi va login ko'rsatiladi. Usiz tanlagich bo'sh
 * ko'rinardi va operator "filtr tushib ketibdi" deb o'ylab, aslida
 * amaldagi filtrni bilmasdan qayta qo'yardi.
 */
export function SubscriberSelect({
  value,
  onChange,
  disabled,
  placeholder,
}: SubscriberSelectProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const allowed = can('subscriber.read')

  const [typed, setTyped] = useState('')
  const [term, setTerm] = useState('')

  // ⚠️ Har bosishda so'rov yubormaslik uchun — `DataTable` qidiruvi bilan
  // AYNI kechikish, ya'ni panel bo'ylab bitta tezlik.
  useEffect(() => {
    if (typed === term) return
    const timer = setTimeout(() => setTerm(typed), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [typed, term])

  const list = useQuery({
    queryKey: queryKeys.subscribers.search(term),
    queryFn: ({ signal }) => searchSubscribers(term, signal),
    enabled: allowed,
    staleTime: 30_000,
  })

  const rows = list.data?.rows ?? []
  const inList = value != null && rows.some((row) => row.id === value)

  /*
    ⚠️ Tanlangan qiymat uchun ALOHIDA so'rov — faqat u ro'yxatda
    BO'LMAGANDA. Kalit `detail` bilan bir xil, ya'ni kartochka yoki
    banner allaqachon so'ragan bo'lsa qo'shimcha so'rov ketmaydi.
  */
  const selected = useQuery({
    queryKey: queryKeys.subscribers.detail(value ?? ''),
    queryFn: () => fetchSubscriber(value ?? ''),
    enabled: allowed && value != null && value !== '' && !inList && !list.isPending,
    staleTime: 5 * 60_000,
    retry: false,
  })

  const shown: readonly Subscriber[] =
    !inList && selected.data ? [selected.data, ...rows] : rows

  const options = shown.map((row) => ({
    value: row.id,
    label: (
      <Space size={4} wrap>
        <Typography.Text strong>{row.login}</Typography.Text>
        {row.displayName ? (
          <Typography.Text type="secondary">{row.displayName}</Typography.Text>
        ) : null}
        {/* Holat KO'RSATILADI, tanlov TO'SILMAYDI: yopilgan abonentning
            yozuvlari jurnalda qoladi va aynan ularni filtrlash kerak */}
        {row.state === 'CLOSED' ? <Tag color="default">{t('subscriberState.CLOSED')}</Tag> : null}
      </Space>
    ),
    title: `${row.login} ${row.displayName ?? ''}`,
  }))

  /*
    ⚠️ Qiymat bor, lekin login hali yo'q — XOM UUID CHIZILMASIN.

    antd tanlagichi ro'yxatda bo'lmagan qiymatni O'Z HOLICHA yozadi,
    ya'ni ekranda `1f2e…` turardi — aynan tuzatilayotgan nuqsonning
    o'zi. Shuning uchun bunday qiymat uchun har doim qator qo'yiladi va
    SABABI aytiladi:

      · hali so'ralyapti          -> «yuklanmoqda»
      · so'rov rad etdi/topilmadi -> «nomi ko'rsatilmadi»

    ⚠️ Qiymatning O'ZI TASHLANMAYDI. Filtr AMALDA — uni ekrandan
    yo'qotish operatorni "filtr tushib ketibdi" degan xulosaga olib
    kelardi va u qisqargan ro'yxatni to'liq deb o'qirdi.
  */
  const unresolved = value != null && value !== '' && !inList && selected.data === undefined
  if (unresolved) {
    options.unshift({
      value,
      label: (
        <Space size={4} wrap>
          <Typography.Text code>{shortId(value)}</Typography.Text>
          <Tag color={selected.isError ? 'warning' : 'default'}>
            {selected.isError ? t('subscriberSelect.unresolved') : t('common.loading')}
          </Tag>
        </Space>
      ),
      title: value,
    })
  }

  const truncated = list.isSuccess && list.data.total > list.data.rows.length

  return (
    <>
      <Select<string | null>
        allowClear
        showSearch
        value={value ?? null}
        onChange={(next) => onChange?.(next ?? null)}
        onSearch={setTyped}
        // ⚠️ Klient filtri O'CHIQ: ro'yxat allaqachon serverda saralangan
        // va kesilgan. Ustidan yana filtrlash terilgan so'zni ikki marta
        // qo'llardi — server topgan qatorlar ekranda YO'QOLARDI.
        filterOption={false}
        onBlur={() => setTyped('')}
        disabled={disabled || !allowed}
        // ⚠️ `isFetching`, `isPending` EMAS: o'chirilgan so'rov
        // TanStack'da BIR UMR `pending` holatida qoladi va tanlagich
        // hech qachon aylanishdan to'xtamasdi.
        loading={list.isFetching || selected.isFetching}
        placeholder={placeholder ?? t('subscriberSelect.placeholder')}
        options={options}
        style={{ width: '100%' }}
        notFoundContent={
          allowed ? t('subscriberSelect.empty') : t('subscriberSelect.noPermission')
        }
      />
      {truncated ? (
        <Typography.Text type="warning" style={{ fontSize: 12 }}>
          {t('subscriberSelect.truncated', {
            shown: list.data.rows.length,
            total: list.data.total,
          })}
        </Typography.Text>
      ) : null}
    </>
  )
}
